import { loadStripe } from '@stripe/stripe-js';
import { supabase } from './supabase';
import { FunctionsHttpError, FunctionsRelayError, FunctionsFetchError } from '@supabase/supabase-js';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

/**
 * Helper function to handle Supabase function errors properly
 */
const handleSupabaseFunctionError = async (error, defaultMessage = 'Function call failed') => {
  let errorMessage = defaultMessage;
  
  if (error instanceof FunctionsHttpError) {
    try {
      const errorResponse = await error.context.json();
      errorMessage = errorResponse.error || errorResponse.message || 'Edge Function returned an error';
      console.error('Function HTTP Error details:', errorResponse);
    } catch (parseError) {
      try {
        const errorText = await error.context.text();
        errorMessage = errorText || 'Edge Function returned an error';
        console.error('Function HTTP Error text:', errorText);
      } catch (textError) {
        console.error('Could not parse error response:', textError);
        errorMessage = 'Edge Function returned a non-2xx status code';
      }
    }
  } else if (error instanceof FunctionsRelayError) {
    errorMessage = 'Function relay error: ' + error.message;
  } else if (error instanceof FunctionsFetchError) {
    errorMessage = 'Function fetch error: ' + error.message;
  } else {
    errorMessage = error.message || defaultMessage;
  }
  
  return errorMessage;
};

// Request deduplication cache
const pendingRequests = new Map();

// Stripe price IDs - these need to be created in your Stripe dashboard
// NOTE: These are placeholder IDs - you need to create actual products/prices in Stripe Dashboard
export const STRIPE_PRICES = {
  premium: 'price_1RmFnwGG5dpuxsMKYJREqXTU', // Replace with actual price ID from Stripe Dashboard
};

// Plan configurations
export const SUBSCRIPTION_PLANS = {
  premium: {
    id: 'premium',
    name: 'All Access Premium',
    price: 5.00,
    priceId: STRIPE_PRICES.premium,
    description: 'Unlimited access to all games across multiple devices',
    features: [
      'Access to 500+ games',
      'Ad-free experience',
      'Play on unlimited devices',
      'New games added weekly',
      'Cancel anytime',
      '7-day free trial'
    ]
  },
  basic: {
    id: 'basic',
    name: 'Basic Gaming',
    price: 2.99,
    priceId: 'price_basic_placeholder', // Replace with actual Stripe price ID
    description: 'Perfect for casual gamers',
    features: [
      'Access to 100+ games',
      'Play on 2 devices',
      'HD streaming quality',
      'Cancel anytime'
    ]
  },
  ultimate: {
    id: 'ultimate',
    name: 'Ultimate Gaming',
    price: 9.99,
    priceId: 'price_ultimate_placeholder', // Replace with actual Stripe price ID
    description: 'For the ultimate gaming experience',
    features: [
      'Access to 1000+ games',
      'Play on unlimited devices',
      '4K streaming quality',
      'Early access to new games',
      'Priority support',
      'Cancel anytime'
    ]
  }
};

/**
 * Create a Stripe subscription with optional 7-day free trial
 */
export const createSubscription = async (planId = 'premium', hasHadTrial = false) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Create a unique key for request deduplication
    const requestKey = `create-subscription-${user.id}-${planId}-${hasHadTrial}`;
    
    // Check if there's already a pending request for this user/plan combination
    if (pendingRequests.has(requestKey)) {
      console.log('🔄 Returning existing pending request for:', requestKey);
      return await pendingRequests.get(requestKey);
    }

    const plan = SUBSCRIPTION_PLANS[planId];
    if (!plan) {
      throw new Error('Invalid plan selected');
    }

    // Get the current session to include auth headers
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    // Prepare request body
    const requestBody = {
      plan: planId,
      priceId: plan.priceId,
      trialDays: hasHadTrial ? 0 : 7 // No trial if user has already had one
    };

    console.log('🚀 Sending request to create-payment-intent:', requestBody);
    console.log('🔧 Stringified body:', JSON.stringify(requestBody));

    // Create the promise and store it in the cache
    const requestPromise = (async () => {
      try {
        // Call Supabase Edge Function to create subscription with proper headers
        // Try using the direct fetch approach instead of supabase.functions.invoke
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Direct fetch error response:', errorText);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        const data = await response.json();
        console.log('✅ Direct fetch success:', data);
        
        if (!data) {
          throw new Error('No data returned from edge function');
        }

        return {
          success: true,
          subscriptionId: data.subscriptionId,
          clientSecret: data.clientSecret,
          customerId: data.customerId
        };
      } catch (fetchError) {
        console.error('Direct fetch failed, trying supabase.functions.invoke:', fetchError);
        
        // Fallback to original method
        const { data, error } = await supabase.functions.invoke('create-payment-intent', {
          body: requestBody,
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json'
          }
        });

        if (error) {
          console.error('Edge function error:', error);
          const errorMessage = await handleSupabaseFunctionError(error, 'Failed to create subscription');
          throw new Error(errorMessage);
        }

        if (!data) {
          throw new Error('No data returned from edge function');
        }

        return {
          success: true,
          subscriptionId: data.subscriptionId,
          clientSecret: data.clientSecret,
          customerId: data.customerId
        };
      }
    })();

    // Store the promise in the cache
    pendingRequests.set(requestKey, requestPromise);

    // Execute the request and clean up the cache
    try {
      const result = await requestPromise;
      return result;
    } finally {
      // Clean up the cache after the request completes (success or failure)
      pendingRequests.delete(requestKey);
    }
  } catch (error) {
    console.error('Create subscription error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Confirm payment and complete subscription setup
 */
export const confirmSubscription = async (clientSecret, paymentMethod) => {
  try {
    const stripe = await stripePromise;
    
    const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: paymentMethod
    });

    if (error) {
      throw new Error(error.message);
    }

    return {
      success: true,
      paymentIntent
    };
  } catch (error) {
    console.error('Confirm subscription error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Confirm subscription and save to database (for non-trial subscriptions)
 */
export const confirmSubscriptionInDatabase = async (subscriptionId, paymentIntentId = null) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    const { data, error } = await supabase.functions.invoke('confirm-subscription', {
      body: JSON.stringify({ 
        subscriptionId,
        paymentIntentId 
      }),
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (error) {
      console.error('Confirm subscription in database error:', error);
      const errorMessage = await handleSupabaseFunctionError(error, 'Failed to confirm subscription');
      throw new Error(errorMessage);
    }

    return {
      success: true,
      subscription: data.subscription
    };
  } catch (error) {
    console.error('Confirm subscription in database error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get current user's subscription status
 */
export const getUserSubscription = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Get the current session to ensure proper authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    // Use edge function to get subscription instead of direct database query
    // This avoids RLS issues and uses the same authentication as other functions
    const { data, error } = await supabase.functions.invoke('get-user-subscription', {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (error) {
      console.error('Get subscription error:', error);
      const errorMessage = await handleSupabaseFunctionError(error, 'Failed to get subscription');
      
      // If the function doesn't exist, return null subscription
      if (errorMessage.includes('not found')) {
        return {
          success: true,
          subscription: null
        };
      }
      
      throw new Error(errorMessage);
    }

    return {
      success: true,
      subscription: data?.subscription || null
    };
  } catch (error) {
    console.error('Get subscription error:', error);
    return {
      success: false,
      error: error.message,
      subscription: null
    };
  }
};

/**
 * Cancel subscription
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    const { data, error } = await supabase.functions.invoke('cancel-subscription', {
      body: JSON.stringify({ subscriptionId }),
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (error) {
      console.error('Cancel subscription error:', error);
      const errorMessage = await handleSupabaseFunctionError(error, 'Failed to cancel subscription');
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Cancel subscription error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update payment method
 */
export const updatePaymentMethod = async (subscriptionId, paymentMethodId) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    const { data, error } = await supabase.functions.invoke('update-payment-method', {
      body: JSON.stringify({ 
        subscriptionId,
        paymentMethodId 
      }),
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (error) {
      console.error('Update payment method error:', error);
      const errorMessage = await handleSupabaseFunctionError(error, 'Failed to update payment method');
      throw new Error(errorMessage);
    }

    return {
      success: true,
      data
    };
  } catch (error) {
    console.error('Update payment method error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get billing history
 */
export const getBillingHistory = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }

    const { data, error } = await supabase.functions.invoke('get-billing-history', {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json'
      }
    });

    if (error) {
      console.error('Get billing history error:', error);
      const errorMessage = await handleSupabaseFunctionError(error, 'Failed to get billing history');
      throw new Error(errorMessage);
    }

    return {
      success: true,
      history: data.history || []
    };
  } catch (error) {
    console.error('Get billing history error:', error);
    return {
      success: false,
      error: error.message,
      history: []
    };
  }
};

/**
 * Check if user has active subscription
 */
export const hasActiveSubscription = async () => {
  try {
    // First check if user is admin (admins get premium access)
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();
      
      if (profile?.role === 'admin') {
        return true;
      }
    }

    const result = await getUserSubscription();
    if (!result.success || !result.subscription) {
      return false;
    }

    const subscription = result.subscription;
    const now = new Date();
    const periodEnd = new Date(subscription.current_period_end);

    // Consider both 'active' and 'trialing' as valid subscription statuses
    const validStatuses = ['active', 'trialing'];
    return validStatuses.includes(subscription.status) && periodEnd > now;
  } catch (error) {
    console.error('Check active subscription error:', error);
    return false;
  }
};

export { stripePromise };
